import { closestCenter, DndContext } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronLeft, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { DifficultyCell } from '@/components/problems/difficulty-cell'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import { Button } from '@/components/ui/button'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { ProgressSummary } from '@/components/ui/progress-summary'
import { useDevice } from '@/hooks/use-mobile'
import type { UserProgressStatus } from '@/lib/progress-status'
import { AddProblemsModal } from './problem-lists/add-problems-modal'
import { DeleteListDialog } from './problem-lists/delete-list-dialog'
import { EditListModal } from './problem-lists/edit-list-modal'
import { ForkStarButton } from './problem-lists/fork-star-button'
import { MergeButton } from './problem-lists/merge-button'
import {
  type DisplayProblem,
  LIST_DETAIL_SORT_POPOVER_OPTIONS,
  useListDetail,
} from './problem-lists/use-list-detail'
import { ShuffleButton } from './shuffle-button'

// ---------------------------------------------------------------------------
// SortableProblemRow — wraps a row with dnd-kit sortable behavior
// ---------------------------------------------------------------------------

function SortableProblemRow({
  id,
  children,
}: {
  id: string
  children: (dragHandleProps: {
    listeners: ReturnType<typeof useSortable>['listeners']
    attributes: ReturnType<typeof useSortable>['attributes']
  }) => React.ReactNode
}) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'bg-muted/70' : ''}
    >
      {children({ listeners, attributes })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// MobileProblemRow — renders a single problem row
// ---------------------------------------------------------------------------

function MobileProblemRow({
  problem,
  solveStatus,
  viaTitle,
  showTags,
  isDragEnabled,
  listId,
  dragHandleProps,
}: {
  problem: DisplayProblem
  solveStatus: UserProgressStatus | null
  viaTitle?: string
  showTags: boolean
  isDragEnabled: boolean
  listId: string
  dragHandleProps?: {
    listeners: ReturnType<typeof useSortable>['listeners']
    attributes: ReturnType<typeof useSortable>['attributes']
  }
}) {
  return (
    <Link
      href={`/editor?problem=${problem.slug}&list=${listId}`}
      className="flex items-center gap-2 border-b px-3 py-3 transition-colors hover:bg-accent active:bg-accent"
    >
      {/* Drag handle — only this element captures drag gestures */}
      {isDragEnabled && (
        <span
          className="text-muted-foreground shrink-0 touch-none"
          {...dragHandleProps?.listeners}
          {...dragHandleProps?.attributes}
          onClick={(e) => e.preventDefault()}
        >
          <GripVertical className="h-4 w-4" />
        </span>
      )}

      {/* Status indicator */}
      <div className="flex w-5 shrink-0 items-center justify-center">
        <ProgressStatusIcon status={solveStatus} viaTitle={viaTitle} />
      </div>

      {/* Problem info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {problem.externalId && (
            <span className="text-muted-foreground shrink-0 text-xs">
              {Number(problem.externalId)}.
            </span>
          )}
          <span className="truncate text-sm font-medium">{problem.title}</span>
        </div>
        {showTags && problem.topics.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {problem.topics.slice(0, 3).map((topic) => (
              <span
                key={topic.slug}
                className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px]"
              >
                {topic.name}
              </span>
            ))}
            {problem.topics.length > 3 && (
              <span className="text-muted-foreground text-[10px]">
                +{problem.topics.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Difficulty */}
      <DifficultyCell difficulty={problem.difficulty} />
    </Link>
  )
}

// ---------------------------------------------------------------------------
// MobileListDetail — main page component
// ---------------------------------------------------------------------------

export function MobileListDetail() {
  const location = useLocation()
  const listId = location.pathname.match(/^\/problems\/lists\/([^/]+)/)?.[1]

  if (!listId) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">List not found.</p>
      </div>
    )
  }

  return <MobileListDetailInner listId={listId} />
}

function MobileListDetailInner({ listId }: { listId: string }) {
  const { isTablet } = useDevice()

  const {
    data,
    isLoading,
    error,
    isSignedIn,
    problems,
    displayProblems,
    solvedCount,
    progressProblems,
    progressRecord,
    filters,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
    availableTopics,
    availableDifficulties,
    availableLists,
    hasSearch,
    hasActiveFilters,
    isDragEnabled,
    sensors,
    handleDragEnd,
    handleSortChange,
    showAddProblems,
    setShowAddProblems,
    handleAddProblemsOpenChange,
    showEdit,
    setShowEdit,
    showDelete,
    setShowDelete,
    dismissed,
    setDismissed,
  } = useListDetail(listId, 'bctci-list-detail-mobile')

  // --- Loading state ---

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  if (error) {
    const isNotFound = error.data?.code === 'NOT_FOUND'
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-16">
        <h2 className="text-lg font-semibold">
          {isNotFound ? 'List not found' : 'Unable to load list'}
        </h2>
        <p className="text-muted-foreground text-center text-sm">
          {isNotFound
            ? "This list doesn't exist or you don't have access to it."
            : 'There was a problem loading this list. Please try again.'}
        </p>
        <div className="flex items-center gap-3">
          {!isNotFound && (
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          )}
          <Link
            href="/problems"
            className="text-primary text-sm hover:underline"
          >
            Back to Problems
          </Link>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { list, stats } = data

  // --- Problem list element ---

  const problemListElement = (
    <div className="flex flex-col">
      {displayProblems.length > 0 ? (
        displayProblems.map((problem) => {
          const rowSolveStatus = problem.solveStatus ?? null
          const viaTitle = data.solvedVia?.[problem.id]?.title

          if (isDragEnabled) {
            return (
              <SortableProblemRow key={problem.id} id={problem.id}>
                {(dragHandleProps) => (
                  <MobileProblemRow
                    problem={problem}
                    solveStatus={rowSolveStatus}
                    viaTitle={viaTitle}
                    showTags={showTags}
                    isDragEnabled={isDragEnabled}
                    listId={listId}
                    dragHandleProps={dragHandleProps}
                  />
                )}
              </SortableProblemRow>
            )
          }

          return (
            <MobileProblemRow
              key={problem.id}
              problem={problem}
              solveStatus={rowSolveStatus}
              viaTitle={viaTitle}
              showTags={showTags}
              isDragEnabled={isDragEnabled}
              listId={listId}
            />
          )
        })
      ) : (
        <div className="text-muted-foreground py-10 text-center text-sm">
          {hasSearch || hasActiveFilters
            ? 'No problems match your search or filters.'
            : 'No problems in this list yet. Tap + to add some.'}
        </div>
      )}
    </div>
  )

  return (
    <div className="flex flex-col pb-6">
      {/* Back link */}
      <div className="px-4 py-3">
        <Link
          href="/problems"
          className="text-muted-foreground inline-flex items-center gap-1 text-sm hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Problems
        </Link>
      </div>

      {/* Header: list name + count */}
      <div className="px-4 pb-2">
        <h1 className="text-xl font-bold">{list.name}</h1>
        <div className="text-muted-foreground mt-0.5 text-sm">
          {stats.total} question{stats.total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 px-4 pb-2">
        <Button
          size="icon-sm"
          variant="outline"
          title="Add problems"
          onClick={() => setShowAddProblems(true)}
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          size="icon-sm"
          variant="outline"
          title="Edit list"
          onClick={() => setShowEdit(true)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {!list.isSystem && (
          <Button
            size="icon-sm"
            variant="outline"
            title="Delete list"
            onClick={() => setShowDelete(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <ForkStarButton sourceType="list" sourceId={listId} />
        <MergeButton sourceType="list" sourceId={listId} />
      </div>

      {/* Description */}
      {list.description && (
        <p className="text-muted-foreground px-4 pb-3 text-sm">
          {list.description}
        </p>
      )}

      {/* ProgressSummary donut chart */}
      <div className="px-4 pb-4">
        <ProgressSummary
          problems={progressProblems}
          progress={progressRecord}
        />
      </div>

      {/* Missing lists banner */}
      {!dismissed && missingLists.length > 0 && (
        <div className="mx-4 mb-3 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <span>Some filters reference lists you don&apos;t have.</span>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="ml-3 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search / filter / sort toolbar */}
      {isTablet ? (
        <div className="flex items-center gap-1 px-4 pb-3">
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <ProblemsSortPopover
            sortBy={list.sortBy}
            sortDir={list.sortDir as 'asc' | 'desc'}
            onSortChange={handleSortChange}
            alwaysActive
            defaultValue="custom"
            sortOptions={LIST_DETAIL_SORT_POPOVER_OPTIONS}
            showTags={showTags}
            onToggleTags={() => setShowTags((v) => !v)}
          />
          <ProblemsFilterPopover
            filters={filters}
            matchMode={matchMode}
            onMatchModeChange={setMatchMode}
            onToggleFilter={toggleFilterValue}
            onOperatorChange={setFilterOperator}
            availableTopics={availableTopics}
            availableDifficulties={availableDifficulties}
            availableLists={availableLists}
            onReset={resetFilters}
          />
          <div className="flex-1" />
          {isSignedIn && <FeaturebaseBugButton />}
          <span className="text-muted-foreground text-xs">
            {solvedCount}/{problems.length} Solved
          </span>
          <ShuffleButton
            problems={displayProblems}
            listId={listId}
            ariaLabel="Solve a random problem from this list"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2 px-4 pb-3">
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <div className="flex items-center gap-2">
            <ProblemsSortPopover
              sortBy={list.sortBy}
              sortDir={list.sortDir as 'asc' | 'desc'}
              onSortChange={handleSortChange}
              alwaysActive
              defaultValue="custom"
              sortOptions={LIST_DETAIL_SORT_POPOVER_OPTIONS}
              showTags={showTags}
              onToggleTags={() => setShowTags((v) => !v)}
            />
            <ProblemsFilterPopover
              filters={filters}
              matchMode={matchMode}
              onMatchModeChange={setMatchMode}
              onToggleFilter={toggleFilterValue}
              onOperatorChange={setFilterOperator}
              availableTopics={availableTopics}
              availableDifficulties={availableDifficulties}
              availableLists={availableLists}
              onReset={resetFilters}
            />
            {isSignedIn && <FeaturebaseBugButton />}
            <span className="text-muted-foreground ml-auto text-xs">
              {solvedCount}/{problems.length} Solved
            </span>
            <ShuffleButton
              problems={displayProblems}
              listId={listId}
              ariaLabel="Solve a random problem from this list"
            />
          </div>
        </div>
      )}

      {/* Problem rows */}
      {isDragEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={displayProblems.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            {problemListElement}
          </SortableContext>
        </DndContext>
      ) : (
        problemListElement
      )}

      {/* Modals */}
      <EditListModal
        open={showEdit}
        onOpenChange={setShowEdit}
        listId={list.id}
        currentName={list.name}
        currentDescription={list.description}
      />
      {!list.isSystem && (
        <DeleteListDialog
          open={showDelete}
          onOpenChange={setShowDelete}
          listId={list.id}
          listName={list.name}
        />
      )}
      <AddProblemsModal
        open={showAddProblems}
        onOpenChange={handleAddProblemsOpenChange}
        listId={listId}
      />
    </div>
  )
}
